import React, { useState, useEffect, useMemo } from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../components/DataTable'
import Button from '../components/Button'
import StatusBadge from '../components/StatusBadge'
import MultiFilter, { FilterOptions } from './components/MultiFilter'
import { GoalEvent, getAllGoalEvents } from '../../services/goalEvents'
import Icon from '../components/Icon'

const RawGoalEventLogs: React.FC = () => {
  const [goalEvents, setGoalEvents] = useState<GoalEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<FilterOptions>({
    students: [],
    teachers: [],
    dateRange: { startDate: '', endDate: '' },
  })

  const getPersonId = (person: GoalEvent['student'] | GoalEvent['teacher']) => {
    if (!person) return null
    return typeof person === 'string' ? person : person._id
  }

  const getPersonName = (
    person: GoalEvent['student'] | GoalEvent['teacher'],
    fallback: string,
  ) => {
    if (!person || typeof person === 'string') return fallback
    return `${person.firstName} ${person.lastName}`
  }

  const getGoalDescription = (goal: GoalEvent['goal']) => {
    if (!goal || typeof goal === 'string') return 'Unknown Goal'
    return goal.description
  }

  // Fetch goal events on component mount
  useEffect(() => {
    fetchGoalEvents()
  }, [])

  const fetchGoalEvents = async () => {
    setLoading(true)
    setError(null)
    try {
      const events = await getAllGoalEvents()
      // Sort by date (newest first)
      const sortedEvents = events.sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
      setGoalEvents(sortedEvents)
    } catch (err: any) {
      console.error('Error fetching goal events:', err)
      setError(err.response?.data?.error || 'Failed to fetch goal events')
    } finally {
      setLoading(false)
    }
  }

  // Filter goal events based on selected filters
  const filteredGoalEvents = useMemo(() => {
    return goalEvents.filter((event) => {
      // Filter by students
      if (filters.students.length > 0) {
        const studentId = getPersonId(event.student)
        if (!studentId || !filters.students.includes(studentId)) {
          return false
        }
      }

      // Filter by teachers
      if (filters.teachers.length > 0) {
        const teacherId = getPersonId(event.teacher)
        if (!teacherId || !filters.teachers.includes(teacherId)) {
          return false
        }
      }

      // Filter by date range (using createdAt field)
      if (filters.dateRange.startDate || filters.dateRange.endDate) {
        const eventDate = new Date(event.createdAt)

        if (filters.dateRange.startDate) {
          const startDate = new Date(filters.dateRange.startDate)
          if (eventDate < startDate) {
            return false
          }
        }

        if (filters.dateRange.endDate) {
          const endDate = new Date(filters.dateRange.endDate)
          // Set end date to end of day to include the entire end date
          endDate.setHours(23, 59, 59, 999)
          if (eventDate > endDate) {
            return false
          }
        }
      }

      return true
    })
  }, [goalEvents, filters])

  // Handle filter changes
  const handleFiltersChange = (newFilters: FilterOptions) => {
    setFilters(newFilters)
  }

  // Handle print functionality
  const handlePrint = () => {
    // Add a slight delay to ensure any state changes are rendered
    setTimeout(() => {
      window.print()
    }, 100)
  }

  // Generate print summary
  const printSummary = useMemo(() => {
    const currentDate = new Date().toLocaleDateString()
    const totalEvents = filteredGoalEvents.length
    const uniqueStudents = new Set(
      filteredGoalEvents
        .map((event) => getPersonId(event.student))
        .filter((id): id is string => Boolean(id)),
    ).size
    const uniqueTeachers = new Set(
      filteredGoalEvents
        .map((event) => getPersonId(event.teacher))
        .filter((id): id is string => Boolean(id)),
    ).size

    return {
      date: currentDate,
      totalEvents,
      uniqueStudents,
      uniqueTeachers,
      dateRange:
        filters.dateRange.startDate || filters.dateRange.endDate
          ? `${filters.dateRange.startDate || 'Beginning'} to ${filters.dateRange.endDate || 'Present'}`
          : 'All dates',
    }
  }, [filteredGoalEvents, filters])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Goal Achieved':
        return 'bg-green-100 text-green-800'
      case 'Making Sufficient Progress':
        return 'bg-blue-100 text-blue-800'
      case 'Not Making Sufficient Progress':
        return 'bg-red-100 text-red-800'
      case 'Not Yet Introduced':
        return 'bg-yellow-100 text-yellow-800'
      case 'Not Applicable To My Subject Area':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Define columns for DataTable
  const columns = useMemo<ColumnDef<GoalEvent>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Date',
        cell: (info) => (
          <div className="text-sm font-medium text-gray-900">
            {formatDate(info.getValue() as string)}
          </div>
        ),
      },
      {
        accessorKey: 'student',
        header: 'Student',
        cell: (info) => {
          const student = info.getValue() as GoalEvent['student']
          return (
            <div className="text-sm text-gray-900">
              {getPersonName(student, 'Unknown Student')}
            </div>
          )
        },
      },
      {
        accessorKey: 'goal',
        header: 'Goal',
        cell: (info) => {
          const goal = info.getValue() as GoalEvent['goal']
          return (
            <div className="text-sm text-gray-900">
              {getGoalDescription(goal)}
            </div>
          )
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => {
          const status = info.getValue() as string

          return (
            <StatusBadge colorClass={getStatusColor(status)}>
              {status}
            </StatusBadge>
          )
        },
      },
      {
        accessorKey: 'teacher',
        header: 'Teacher',
        cell: (info) => {
          const teacher = info.getValue() as GoalEvent['teacher']
          return (
            <div className="text-sm text-gray-900">
              {getPersonName(teacher, 'Unknown Teacher')}
            </div>
          )
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Logged At',
        cell: (info) => (
          <div className="text-sm text-gray-500">
            {formatDateTime(info.getValue() as string)}
          </div>
        ),
      },
    ],
    [],
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Print Styles */}
      <style>{`
        @media print {
          * { overflow: visible !important; }

          /* Hide non-essential elements */
          .no-print {
            display: none !important;
          }

          /* Show print-only elements */
          .print-only {
            display: block !important;
          }

          /* Ensure the main content is visible */
          body {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Print header styling */
          .print-header {
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
            margin-bottom: 20px;
          }

          /* Force black text for better printing */
          .print-text {
            color: #000 !important;
          }

          /* Print summary styles */
          .print-summary {
            background: #f9f9f9 !important;
            border: 1px solid #ccc !important;
            padding: 15px !important;
            margin-bottom: 20px !important;
            page-break-after: avoid !important;
          }

          /* Print list styles */
          .print-event-list {
            page-break-inside: auto;
          }

          .print-event-item {
            border: 1px solid #ccc;
            padding: 10px;
            margin-bottom: 10px;
            background: white;
            page-break-inside: avoid;
          }

          /* Status styling for print */
          .status-achieved { background-color: #dcfce7 !important; color: #166534 !important; }
          .status-progress { background-color: #dbeafe !important; color: #1e40af !important; }
          .status-not-progress { background-color: #fee2e2 !important; color: #991b1b !important; }
          .status-not-introduced { background-color: #fef3c7 !important; color: #92400e !important; }
          .status-na { background-color: #f3f4f6 !important; color: #1f2937 !important; }
        }

        @page {
          margin: 0.5in;
          size: letter;
        }
      `}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 print-text">
            Progress Report Logs
          </h1>
          <p className="text-gray-600 print-text">
            View all goal events logged for your students
          </p>
        </div>
        {/* Print Button */}
        <Button
          onClick={handlePrint}
          variant="secondary"
          className="ml-auto no-print"
          disabled={loading || filteredGoalEvents.length === 0}
        >
          <Icon type="printer" className="w-4 h-4 mr-2" />
          Print Logs
        </Button>
      </div>

      {/* Print-only Summary */}
      <div className="hidden print:block print-summary">
        <div className="print-header">
          <h2 className="text-xl font-bold print-text">
            Progress Report Logs Summary
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm print-text">
          <div>
            <strong>Report Date:</strong> {printSummary.date}
          </div>
          <div>
            <strong>Date Range:</strong> {printSummary.dateRange}
          </div>
          <div>
            <strong>Total Events:</strong> {printSummary.totalEvents}
          </div>
          <div>
            <strong>Unique Students:</strong> {printSummary.uniqueStudents}
          </div>
          <div>
            <strong>Unique Teachers:</strong> {printSummary.uniqueTeachers}
          </div>
        </div>
      </div>

      {/* Print-only Event List */}
      <div className="hidden print:block print-event-list">
        {filteredGoalEvents.map((event, index) => (
          <div key={event._id || index} className="print-event-item">
            <div className="grid grid-cols-2 gap-4 text-sm print-text">
              <div>
                <strong>Date:</strong> {formatDate(event.createdAt)}
              </div>
              <div>
                <strong>Student:</strong>{' '}
                {getPersonName(event.student, 'Unknown Student')}
              </div>
              <div className="col-span-2">
                <strong>Goal:</strong> {getGoalDescription(event.goal)}
              </div>
              <div>
                <strong>Teacher:</strong>{' '}
                {getPersonName(event.teacher, 'Unknown Teacher')}
              </div>
              <div>
                <strong>Status:</strong>
                <span
                  className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                    event.status === 'Goal Achieved'
                      ? 'status-achieved'
                      : event.status === 'Making Sufficient Progress'
                        ? 'status-progress'
                        : event.status === 'Not Making Sufficient Progress'
                          ? 'status-not-progress'
                          : event.status === 'Not Yet Introduced'
                            ? 'status-not-introduced'
                            : 'status-na'
                  }`}
                >
                  {event.status}
                </span>
              </div>
              <div>
                <strong>Logged At:</strong> {formatDateTime(event.createdAt)}
              </div>
              {event.notes && (
                <div className="col-span-2">
                  <strong>Notes:</strong> {event.notes}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg no-print">
          {error}
        </div>
      )}

      {/* Multi Filter Component - Hidden in print */}
      <div className="no-print">
        <MultiFilter
          goalEvents={goalEvents}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      </div>

      {/* Progress Reports Table - Hidden in print */}
      <div className="no-print">
        <DataTable
          data={filteredGoalEvents}
          columns={columns}
          searchPlaceholder="Search goal events..."
          emptyStateTitle="No goal events found"
          emptyStateDescription="No goal events have been logged yet."
          emptyStateIcon={
            <Icon type="document-text" className="mx-auto h-12 w-12 text-gray-400" />
          }
          loading={loading}
        />
      </div>
    </div>
  )
}

export default RawGoalEventLogs
